import { defineStore } from 'pinia';
import { profilePictureObj } from '@/interfaces/userInfo';
import jwtInterceptor from "@/plugins/jwtInterceptor";
import { UserApi } from '@/interfaces/api/userApi';
import { isNoLinkedAccountError } from '@/utils/accountLinkError';

type PicBolb = {
    type: string;
    result: string;
};

export const useUserStore = defineStore('user', {
    state: (): {
        user: UserApi | null,
        pictureBlobUrl: PicBolb | null,
        iCalFeed: string[],
        accountLinkError: boolean,
    } => ({
        user: {
            id: 0,
            CustomCarImage: null,
            CustomPhoneImage: null,
            CustomUserImage: null,
            Email: null,
            Firstname: 'Guest',
            Homebase: null,
            Lastname: 'User',
            Phonenumber: null,
            Workgroup: null,
            authenticationAlias: 'guest',
            isESignSet: null,
            isExportIcalHolidayEntryRequired: false,
            isPushNotificationOnlyDuringClockedInHours: null,
            isShowFullFirstNameRequired: null,
            numberOfMinutesReminderBeforeWorkStarts: 10,
            roles: [],
            status: null,
            usersign: 'GUEST',
        },
        pictureBlobUrl: null,
        iCalFeed: [],
        accountLinkError: false,
    }),
    persist: {
        enabled: true,
    },
    getters: {
        getUserInfo(state) {
            return state.user;
        },
        getUserProfilePicture(state): null | profilePictureObj {
            return state.pictureBlobUrl;
        },
        getUsersICalFeeds(state) {
            return state.iCalFeed;
        },
        getAccountLinkError(state) {
            return state.accountLinkError;
        },
    },
    actions: {
        async renewData() {
            const response = await jwtInterceptor.get('v1/user')
                .catch(error => {
                    if (isNoLinkedAccountError(error)) {
                        this.accountLinkError = true;
                        console.warn('USER_STORE: authenticated identity has no linked PCP account');
                        return;
                    }
                    console.warn('USER_STORE: Could not the user data from the server due to: ' + error);
                });
            if (response && response.data) {
                this.accountLinkError = false;
                this.user = response.data;
                // getting profile pic can only be done when user data is loaded (need user sign
                this.setProfilePictureBlob()
            }
        },
        setProfilePictureBlob() {
            console.debug('USER-STORE: setProfilePictureBlob')
            const src = 'v1/user/ms-profile/' + this.user?.authenticationAlias + '@nayak.aero';
            jwtInterceptor.request({ url: src, method: "get", responseType: "blob" })
                .then(
                    (blob) => {
                        this.pictureBlobUrl = { "type": "blob", "result": URL.createObjectURL(blob.data) };
                    }
                )
                .catch(() => {
                    const fi = (this.user?.Firstname || '').charAt(0)
                    const li = (this.user?.Lastname || '').charAt(0)
                    this.pictureBlobUrl = { "type": "initials", "result": `${fi}${li}` };
                })

        },
        loadUserICalFeeds() {
            jwtInterceptor.get('v1/user/ical').then((response) => {
                if (response && response.data) {
                    this.iCalFeed = response.data;
                }
            }).catch((error) => {
                console.warn("Error on Ical load: " + error.message)
            })
        },
        removeICalFeedFromUser(feed) {
            jwtInterceptor.delete('v1/user/ical/' + feed.id).then(() => {
                this.loadUserICalFeeds();
            }).catch((error) => {
                console.warn("Error on Ical delete: " + error.message)
            });
        },
        setDemoAccount() {
            this.user = {
                id: -1,
                CustomCarImage: null,
                CustomPhoneImage: null,
                CustomUserImage: null,
                Email: null,
                Firstname: 'Demo',
                Homebase: null,
                Lastname: 'User',
                Phonenumber: null,
                Workgroup: null,
                authenticationAlias: 'demo',
                isESignSet: null,
                isExportIcalHolidayEntryRequired: false,
                isPushNotificationOnlyDuringClockedInHours: null,
                isShowFullFirstNameRequired: null,
                numberOfMinutesReminderBeforeWorkStarts: 10,
                roles: ['ROLE_USER'],
                status: null,
                usersign: 'DEMOUSER',
            }
        }
    },
})
